class FormSelect {
    constructor(el) {
        this.root = el;

        if (this.root) {
            this.init();
        }
    }

    init() {
        this.multiple = this.root.dataset.multiple === '1';
        this.keepSelection = this.root.dataset.keepSelection === '1';
        this.currentValue = this.multiple
            ? this.parseCurrentValues(this.root.dataset.currentValue)
            : this.root.dataset.currentValue || '';
        this.elementWrapper = this.root.querySelector(
            '[js-custom-select-element-wrapper]'
        );
        this.combobox = this.elementWrapper?.querySelector(
            '[js-form-select-trigger]'
        );
        this.activeElementLabel = this.combobox?.querySelector(
            '[js-active-element-label]'
        );
        this.listbox = this.elementWrapper?.querySelector(
            '[js-form-select-listbox]'
        );
        this.scrollPanel = this.listbox?.querySelector(
            '[js-form-select-scroll]'
        );
        this.selectItems = Array.from(
            this.elementWrapper?.querySelectorAll('[js-select-item]') ?? []
        );

        this.nativeSelect = this.root.querySelector('[js-form-select-native]');

        this.baseValue = this.root.dataset.baseValue;
        this.expandedClass = '-expanded';
        this.checkedClass = '-checked';
        this.highlightClass = 'is-highlighted';
        this.isOpen = false;

        this.handleDocumentPointerDown =
            this.handleDocumentPointerDown.bind(this);
        this.handleComboboxKeydown = this.handleComboboxKeydown.bind(this);
        this.handleComboboxClick = this.handleComboboxClick.bind(this);
        this.handleListboxKeydown = this.handleListboxKeydown.bind(this);
        this.handleSelectItemClick = this.handleSelectItemClick.bind(this);
        this.handleNativeChange = this.handleNativeChange.bind(this);

        this.events();
        this.syncNativeSelect();
        this.updateActiveLabelFromSelection();
    }

    parseCurrentValues(rawValue) {
        if (!rawValue) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawValue);
            if (Array.isArray(parsed)) {
                return parsed.map(String).filter(Boolean);
            }
        } catch (error) {
            // Fall through to comma-separated / single value parsing.
        }

        return String(rawValue)
            .split(',')
            .map(value => value.trim())
            .filter(Boolean);
    }

    events() {
        if (this.combobox) {
            this.combobox.addEventListener('click', this.handleComboboxClick);
            this.combobox.addEventListener(
                'keydown',
                this.handleComboboxKeydown
            );
        }

        if (this.listbox) {
            this.listbox.addEventListener('keydown', this.handleListboxKeydown);
        }

        this.selectItems.forEach(item => {
            item.addEventListener('click', this.handleSelectItemClick);
        });

        if (this.nativeSelect) {
            this.nativeSelect.addEventListener('change', this.handleNativeChange);
        }
    }

    handleNativeChange() {
        if (this.isSyncingNative) {
            return;
        }

        this.syncFromNativeSelect();
    }

    syncFromNativeSelect() {
        if (!this.nativeSelect) {
            return;
        }

        if (this.multiple) {
            this.currentValue = Array.from(this.nativeSelect.selectedOptions)
                .map(option => option.value)
                .filter(Boolean);
        } else {
            this.currentValue = this.nativeSelect.value || '';
        }

        this.selectItems.forEach(item => {
            const selected = this.multiple
                ? this.currentValue.includes(item.dataset.value)
                : item.dataset.value === this.currentValue;

            item.classList.toggle(this.checkedClass, selected);
            item.setAttribute('aria-selected', selected ? 'true' : 'false');
        });

        this.updateActiveLabelFromSelection();
    }

    handleComboboxClick(e) {
        e.preventDefault();
        this.toggle();
    }

    handleDocumentPointerDown(e) {
        if (!this.root.contains(e.target)) {
            this.close();
        }
    }

    handleComboboxKeydown(e) {
        switch (e.key) {
            case 'ArrowDown':
            case 'Down':
                e.preventDefault();
                if (!this.isOpen) {
                    this.open({ focusList: true });
                } else {
                    this.focusNextOption();
                }
                break;
            case 'ArrowUp':
            case 'Up':
                e.preventDefault();
                if (!this.isOpen) {
                    this.open({ focusList: true });
                } else {
                    this.focusPreviousOption();
                }
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (!this.isOpen) {
                    this.open({ focusList: true });
                }
                break;
            case 'Escape':
            case 'Esc':
                e.preventDefault();
                this.close();
                break;
            case 'Tab':
                if (this.isOpen) {
                    e.preventDefault();
                    this.focusOption(
                        this.getFocusedItem() ||
                            this.getSelectedItem() ||
                            this.selectItems[0]
                    );
                }
                break;
            default:
                break;
        }
    }

    handleListboxKeydown(e) {
        const item = e.target.closest('[js-select-item]');
        if (!item) {
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
            case 'Down':
                e.preventDefault();
                this.focusNextOption();
                break;
            case 'ArrowUp':
            case 'Up':
                e.preventDefault();
                this.focusPreviousOption();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                this.applySelection(item);
                break;
            case 'Escape':
            case 'Esc':
                e.preventDefault();
                this.close({ returnFocus: true });
                break;
            case 'Tab':
                this.close();
                break;
            default:
                break;
        }
    }

    handleSelectItemClick(e) {
        const item = e.target.closest('[js-select-item]');
        if (!item) return;

        e.preventDefault();
        e.stopPropagation();
        this.applySelection(item);
        // Pointer selection must not leave keyboard highlight on another row
        // (is-highlighted styles override -checked background/border).
        this.clearHighlight();
    }

    toggle() {
        if (this.isOpen) {
            this.close({ returnFocus: true });
        } else {
            this.open();
        }
    }

    open({ focusList = false } = {}) {
        if (!this.elementWrapper || !this.combobox || !this.listbox) return;

        this.isOpen = true;
        this.elementWrapper.classList.add(this.expandedClass);
        this.combobox.setAttribute('aria-expanded', 'true');
        this.listbox.setAttribute('aria-hidden', 'false');
        if (this.scrollPanel) {
            this.scrollPanel.scrollTop = 0;
        }

        requestAnimationFrame(() => {
            document.addEventListener(
                'pointerdown',
                this.handleDocumentPointerDown
            );

            if (focusList) {
                this.focusOption(
                    this.getSelectedItem() ||
                        this.getFocusedItem() ||
                        this.selectItems[0]
                );
            } else {
                const initialItem =
                    this.getSelectedItem() || this.selectItems[0];

                if (initialItem) {
                    // Pointer open: set activedescendant without is-highlighted,
                    // so the first row does not keep a sticky black border.
                    this.combobox.setAttribute(
                        'aria-activedescendant',
                        initialItem.id
                    );
                    initialItem.scrollIntoView({ block: 'nearest' });
                }
            }
        });
    }

    close({ returnFocus = false } = {}) {
        if (!this.elementWrapper || !this.combobox || !this.listbox) return;

        this.isOpen = false;
        this.elementWrapper.classList.remove(this.expandedClass);
        this.combobox.setAttribute('aria-expanded', 'false');
        this.listbox.setAttribute('aria-hidden', 'true');
        this.combobox.removeAttribute('aria-activedescendant');
        this.clearHighlight();
        this.selectItems.forEach(item => item.setAttribute('tabindex', '-1'));
        document.removeEventListener(
            'pointerdown',
            this.handleDocumentPointerDown
        );

        if (returnFocus) {
            this.combobox.focus();
        } else if (
            document.activeElement instanceof HTMLElement
            && this.root.contains(document.activeElement)
        ) {
            document.activeElement.blur();
        }
    }

    getSelectedItem() {
        return (
            this.selectItems.find(item =>
                item.classList.contains(this.checkedClass)
            ) ?? null
        );
    }

    getSelectedItems() {
        return this.selectItems.filter(item =>
            item.classList.contains(this.checkedClass)
        );
    }

    getFocusedItem() {
        return (
            this.selectItems.find(item => item === document.activeElement) ??
            null
        );
    }

    getHighlightedItem() {
        return (
            this.selectItems.find(item =>
                item.classList.contains(this.highlightClass)
            ) ?? null
        );
    }

    focusOption(item) {
        if (!item || !this.combobox) return;

        this.selectItems.forEach(option => {
            option.setAttribute('tabindex', '-1');
        });

        item.setAttribute('tabindex', '0');
        this.highlightOption(item);
        item.focus();
        item.scrollIntoView({ block: 'nearest' });
    }

    highlightOption(item) {
        if (!item || !this.combobox) return;

        this.clearHighlight();
        item.classList.add(this.highlightClass);
        this.combobox.setAttribute('aria-activedescendant', item.id);
    }

    clearHighlight() {
        this.selectItems.forEach(item => {
            item.classList.remove(this.highlightClass);
        });
    }

    focusNextOption() {
        if (!this.selectItems.length) return;

        const currentIndex = this.selectItems.findIndex(
            item => item === document.activeElement
        );
        const nextIndex =
            currentIndex < this.selectItems.length - 1 ? currentIndex + 1 : 0;

        this.focusOption(this.selectItems[nextIndex]);
    }

    focusPreviousOption() {
        if (!this.selectItems.length) return;

        const currentIndex = this.selectItems.findIndex(
            item => item === document.activeElement
        );
        const previousIndex =
            currentIndex > 0 ? currentIndex - 1 : this.selectItems.length - 1;

        this.focusOption(this.selectItems[previousIndex]);
    }

    applySelection(item) {
        if (!item) return;

        if (this.multiple) {
            this.applyMultipleSelection(item);
            return;
        }

        const isChecked = item.classList.contains(this.checkedClass);
        const isRequired =
            Boolean(this.nativeSelect?.required) ||
            this.nativeSelect?.getAttribute('aria-required') === 'true';

        if ((this.keepSelection || isRequired) && isChecked) {
            this.close({ returnFocus: true });
            return;
        }

        this.selectItems.forEach(element => {
            element.classList.remove(this.checkedClass);
            element.setAttribute('aria-selected', 'false');
        });

        if (!isChecked) {
            const { value, label, triggerLabel } = item.dataset;
            // Fallback do labela, gdy value jest puste (złe mapowanie opcji ACF).
            this.currentValue =
                value !== undefined && value !== ''
                    ? value
                    : label || '';
            item.classList.add(this.checkedClass);
            item.setAttribute('aria-selected', 'true');
            this.updateActiveLabel(triggerLabel || label);
        } else {
            this.currentValue = '';
            item.classList.remove(this.checkedClass);
            item.setAttribute('aria-selected', 'false');
            this.updateActiveLabel(this.baseValue);
        }

        this.syncNativeSelect();
        this.dispatchNativeChange();
        this.close();
    }

    applyMultipleSelection(item) {
        const isChecked = item.classList.contains(this.checkedClass);

        if (isChecked) {
            item.classList.remove(this.checkedClass);
            item.setAttribute('aria-selected', 'false');
        } else {
            item.classList.add(this.checkedClass);
            item.setAttribute('aria-selected', 'true');
        }

        this.currentValue = this.getSelectedItems().map(
            selectedItem => selectedItem.dataset.value ?? ''
        );
        this.syncNativeSelect();
        this.updateActiveLabelFromSelection();
        this.dispatchNativeChange();
    }

    dispatchNativeChange() {
        if (!this.nativeSelect) {
            return;
        }

        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncNativeSelect() {
        if (!this.nativeSelect) return;

        this.isSyncingNative = true;

        try {
            if (this.multiple) {
                const selectedValues = (
                    Array.isArray(this.currentValue) ? this.currentValue : []
                ).map(String);

                Array.from(this.nativeSelect.options).forEach(option => {
                    option.selected = selectedValues.includes(
                        String(option.value)
                    );
                });
                return;
            }

            const value =
                this.currentValue == null ? '' : String(this.currentValue);

            // Jak w multiselect: jawne selected na optionach (nie tylko .value).
            Array.from(this.nativeSelect.options).forEach(option => {
                option.selected = String(option.value) === value;
            });

            // Dopnij .value na wypadek gdyby selected nie zaktualizowało value.
            if (String(this.nativeSelect.value) !== value) {
                this.nativeSelect.value = value;
            }
        } finally {
            this.isSyncingNative = false;
        }
    }

    updateActiveLabelFromSelection() {
        if (!this.multiple) {
            if (!this.currentValue) {
                this.updateActiveLabel(this.baseValue);
                return;
            }

            const selectedItem = this.getSelectedItem();
            this.updateActiveLabel(
                selectedItem?.dataset.triggerLabel ||
                    selectedItem?.dataset.label ||
                    this.baseValue
            );
            return;
        }

        const selectedItems = this.getSelectedItems();

        if (selectedItems.length === 0) {
            this.updateActiveLabel(this.baseValue);
            return;
        }

        if (selectedItems.length === 1) {
            this.updateActiveLabel(
                selectedItems[0].dataset.triggerLabel ||
                    selectedItems[0].dataset.label
            );
            return;
        }

        this.updateActiveLabel(`${selectedItems.length} wybranych`);
    }

    updateActiveLabel(label) {
        if (!this.activeElementLabel) return;
        this.activeElementLabel.textContent = label;
    }
}

const initFormSelects = (root = document) => {
    const scope = root instanceof Element ? root : document;

    scope.querySelectorAll('[js-form-select]').forEach(element => {
        if (element.dataset.formSelectInit === '1') {
            return;
        }

        element.dataset.formSelectInit = '1';
        new FormSelect(element);
    });
};

initFormSelects(document);
window.mdaInitFormSelects = initFormSelects;
